import axios, { AxiosInstance } from 'axios'
import { authStore } from './stores/auth'
import { disconnectAllWebSockets } from './services/websocketUtils'
import { getTokenExpiry } from './services/auth/jwtUtils'

axios.defaults.withCredentials = true

const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT),
  withCredentials: true,
})

// quem não precisa autenticar
const noAuthRoutes = ['/verify-email/', '/token/', '/token/refresh/', '/onboarding/sessions/']

// controle do refresh simultâneo
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

const toPlainHeaders = (headers: unknown): Record<string, unknown> => {
  if (!headers) return {}
  if (typeof headers === 'object' && headers !== null) {
    if (typeof (headers as any).toJSON === 'function') {
      return (headers as any).toJSON()
    }
    return { ...(headers as Record<string, unknown>) }
  }
  return {}
}

const sanitizeHeaders = (headers: unknown): Record<string, unknown> => {
  const plainHeaders = toPlainHeaders(headers)
  const headerNamesToRedact = ['authorization', 'proxy-authorization']
  Object.keys(plainHeaders).forEach((key) => {
    if (headerNamesToRedact.includes(key.toLowerCase())) {
      plainHeaders[key] = '[REDACTED]'
    }
  })
  return plainHeaders
}

const summarizeData = (data: unknown): string | string[] => {
  if (Array.isArray(data)) {
    return `Array(${data.length})`
  }

  if (data && typeof data === 'object') {
    return Object.keys(data as Record<string, unknown>)
  }

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return summarizeData(parsed)
    } catch (_) {
      return 'String(length: ' + data.length + ')'
    }
  }

  if (data === undefined || data === null) {
    return 'No payload'
  }

  return typeof data
}

apiClient.interceptors.request.use(
  async function (config) {
    const auth = authStore()
    const token = auth.token
    const isNoAuthRoute = noAuthRoutes.some((route) => config.url?.includes(route))

    // 🔍 DEBUG: Log todas as requisições updateLeadStage
    if (import.meta.env.DEV && config.url?.includes('updateLeadStage')) {
      console.group('🌐 [http-common] Axios Request to updateLeadStage')
      console.log('Method:', config.method?.toUpperCase())
      console.log('Base URL:', config.baseURL)
      console.log('Relative URL:', config.url)
      console.log('Full URL:', (config.baseURL || '') + (config.url || ''))
      console.log('Headers:', sanitizeHeaders(config.headers))
      console.log('Payload summary:', summarizeData(config.data))
      console.groupEnd()
    }

    // Check token expiry before each request
    if (token && !isNoAuthRoute) {
      const expiry = getTokenExpiry(token)
      if (expiry && Date.now() > expiry - 60000) {
        // refresh if <1min left
        try {
          if (!isRefreshing) {
            isRefreshing = true
            const resp = await auth.refresh_app_layout()
            auth.updateToken(resp.data.access, resp.data.refresh, new Date().toISOString())
            isRefreshing = false
          }
        } catch (e) {
          isRefreshing = false
          disconnectAllWebSockets()
          auth.removeToken()
          window.location.href = '/auth'
          return Promise.reject(e)
        }
      }
      config.headers.Authorization = `Bearer ${auth.token}`
    }

    // Add X-Company-ID header if company is selected and it's not a no-auth route
    // Ensure we don't send "null" or "undefined" as strings
    if (auth.cmpId && auth.cmpId !== 'null' && auth.cmpId !== 'undefined' && !isNoAuthRoute) {
      config.headers['X-Company-ID'] = String(auth.cmpId)
    }

    if (import.meta.env.DEV) {
      console.log(`🌐 [http-common] Request: ${config.method?.toUpperCase()} ${config.url}`, {
        headers: sanitizeHeaders(config.headers),
      })
    }

    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data'
    } else {
      config.headers['Content-Type'] = 'application/json'
    }

    return config
  },
  function (error) {
    return Promise.reject(error)
  },
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // 🔍 DEBUG: Log detalhado de erros 404 no updateLeadStage
    if (import.meta.env.DEV && error.response?.status === 404 && error.config?.url?.includes('updateLeadStage')) {
      console.group('❌ [http-common] 404 Error on updateLeadStage')
      console.error('Status:', error.response.status, error.response.statusText)
      console.error('Request URL:', error.config.url)
      console.error('Full URL:', (error.config.baseURL || '') + (error.config.url || ''))
      console.error('Method:', error.config.method?.toUpperCase())
      console.error('Response Data summary:', summarizeData(error.response.data))
      console.error('Response Headers:', sanitizeHeaders(error.response.headers))
      console.groupEnd()
    }

    const requestUrl = originalRequest?.url || ''
    const isAuthBootstrapRoute = noAuthRoutes.some((route) => requestUrl.includes(route))

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthBootstrapRoute) {
      const auth = authStore()
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(originalRequest))
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const resp = await auth.refresh_app_layout()
        const newToken = resp.data.access

        refreshSubscribers.forEach((callback) => callback(newToken))
        refreshSubscribers = []

        return apiClient(originalRequest)
      } catch (refreshError) {
        disconnectAllWebSockets()
        auth.removeToken()
        window.location.href = '/auth'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Handle 403 Company context required
    if (error.response?.status === 403 && error.response?.data?.detail?.includes('Company context required')) {
      window.location.href = '/select-company'
      return Promise.reject(error)
    }

    return Promise.reject(error)
  },
)

export default apiClient
